import { call, fork, put, select, takeLatest, delay, takeLeading } from 'redux-saga/effects';
import { Logger } from '@api/services/errors/appLogger';

import { ApplicationState as State, getRouterStore } from '@api/store';
import { loadChannels, loadSingleChannel, exportChannels, checkTaskStatus } from '@api/services/ElasticApi';
import { AccessLevel, ExportStatus } from '@dev/ee-api/ee-api-models';
import { push as Push, RouterState } from 'redux-first-history';
import {
    LoadChannelsSuccess,
    LoadChannelsError,
    LoadSingleChannelSuccess,
    LoadSingleChannelError,
    ExportChannelsError,
    ExportChannelsStart,
    ExportChannelsSuccess,
} from '@api/store/channels/actions';
import {
    LoadChannelsStartAction,
    LoadSingleChannelStartAction,
    ExportChannelsAction,
    FindChannelsAction,
    ChannelsActionTypes,
} from '@api/store/channels/types';

import { ShowToast, HideToast } from '@api/store/toast/actions';

import { parse, stringify } from 'query-string';
import { omit } from 'lodash-es';
import { getAccessLevels } from '@api/store/account/actions';

const MAX_PER_PAGE = 10;

const getActivePage = (state: State): number => state.channels.activePage;
const getPerPage = (state: State): number => state.channels.perPage;

const getChannels = (state: State) => state.channels.channels || [];
const getTotalCount = (state: State) => state.channels.totalCount;

const getOffsetAndLimit = (activePage: number, perPage?: number, payload?: any): { offset: number; limit: number } => {
    const itemsOnPage = perPage || MAX_PER_PAGE;
    const offset = payload?.offset ?? itemsOnPage * activePage - itemsOnPage;
    const limit = payload?.limit ?? perPage;

    return { offset, limit };
};

function* handleURLQuery(payload, type: string, activePage: number, perPage: number): any {
    const { location }: RouterState = yield select(getRouterStore);
    let queryParams = parse(location.search);

    if (type === ChannelsActionTypes.SEARCH_FOR_CHANNELS) {
        if (payload && payload.search) {
            queryParams.search = payload.search;
        } else {
            queryParams = omit(queryParams, 'search');
        }
    }

    activePage < 2 ? (queryParams = omit(queryParams, 'page')) : (queryParams.page = activePage.toString());

    const maxElements = parseInt(queryParams.perPage as string);

    if (maxElements !== perPage && perPage !== MAX_PER_PAGE) {
        queryParams.perPage = perPage.toString();
    }

    if (perPage === MAX_PER_PAGE) {
        queryParams = omit(queryParams, 'perPage');
    }

    yield put(Push({ search: stringify(queryParams) }));
}

function* fetchChannels(action: LoadChannelsStartAction | FindChannelsAction): any {
    const al = yield select(getAccessLevels);

    if (!al.includes(AccessLevel.ViewChannels)) return;

    try {
        const { payload, type } = action;
        const { activePage = yield select(getActivePage), perPage = yield select(getPerPage), from, to } = payload;
        let totalCount = yield select(getTotalCount);

        if (type === ChannelsActionTypes.SEARCH_FOR_CHANNELS) {
            yield delay(500);
        }
        if (!payload.skipSagaURL) {
            yield handleURLQuery(payload, type, activePage, perPage);
        }

        const { offset, limit } = getOffsetAndLimit(activePage, perPage, payload);

        const storeChannels = yield select(getChannels);

        const searchStr = payload?.search?.toLowerCase();
        const resp = yield call(loadChannels, searchStr, limit, offset, from, to);

        const respChannels = resp.data;

        totalCount = resp.headers?.['x-total-count'];

        const hasMore = respChannels.length === limit;

        const previousChannels = offset === 0 ? [] : storeChannels;

        const params = {
            channels: [...previousChannels, ...respChannels],
            channelsPreview: respChannels,
            totalCount,
            hasMore,
            activePage,
            perPage,
            search: payload.search,
        };

        yield put(LoadChannelsSuccess(params));
    } catch (e) {
        const error = e?.response?.data?.Error ? e.response.data.Error : e.message;
        Logger.error(error);

        yield put(LoadChannelsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* fetchSingleChannel(action: LoadSingleChannelStartAction): any {
    try {
        const { payload } = action;

        const singleChannel = yield call(loadSingleChannel, payload.name);
        yield put(LoadSingleChannelSuccess(singleChannel?.data));
    } catch (e) {
        Logger.error(e);
        yield put(LoadSingleChannelError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* exportChannel(action: ExportChannelsAction): any {
    const { payload } = action;

    yield put(ExportChannelsStart());

    try {
        const resp = yield call(exportChannels, payload);
        let checkExportStatus = true;

        while (checkExportStatus) {
            const statusResp = yield call(checkTaskStatus, resp.data.PublicExportID);
            const status: ExportStatus = statusResp.data;

            if (status === ExportStatus.Ready) {
                checkExportStatus = false;
            }

            if (status === ExportStatus.Error || status === ExportStatus.Expired) {
                throw new Error(statusResp);
            }

            delay(500);
        }

        yield put(ExportChannelsSuccess());
        yield put(
            ShowToast({
                severity: 'info',
                message: `${payload.channelNames ? payload.channelNames.length : 'All'} channel(s) exported`,
                duration: 6000,
                onClose: HideToast,
            })
        );
        yield put(Push('/exports'));
    } catch (e) {
        yield put(ExportChannelsError());
        yield put(
            ShowToast({
                severity: 'error',
                message: `Error: ${e.response.data.Error}`,
                duration: 6000,
                onClose: HideToast,
            })
        );
    }
}

function* watchFetchChannels(): any {
    yield takeLeading(ChannelsActionTypes.LOAD_ALL, fetchChannels);
}

function* watchFetchSingleChannel(): any {
    yield takeLatest(ChannelsActionTypes.LOAD_SINGLECHANNEL_START, fetchSingleChannel);
}

function* watchExportChannels(): any {
    yield takeLatest(ChannelsActionTypes.EXPORT_CHANNELS, exportChannel);
}

function* watchFindChannels(): any {
    yield takeLatest(ChannelsActionTypes.SEARCH_FOR_CHANNELS, fetchChannels);
}

const ChannelsSagas = [fork(watchFetchChannels), fork(watchExportChannels), fork(watchFindChannels), fork(watchFetchSingleChannel)];

export default ChannelsSagas;
